import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { IconUpload, IconTrash, IconPackage, IconPencil } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useReleases,
  useCreateRelease,
  useUpdateRelease,
  useDeleteRelease,
} from "@/api/hooks/use-releases";
import { asReleaseId, type ReleaseId } from "@/types/identifiers";

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function EditableName({
  releaseId,
  initialName,
}: {
  releaseId: ReleaseId;
  initialName: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateMut = useUpdateRelease();

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === initialName) {
      setName(initialName);
      setIsEditing(false);
      return;
    }

    updateMut.mutate(
      { releaseId, name: trimmed },
      {
        onSuccess: () => {
          toast.success("Release renamed");
          setIsEditing(false);
        },
        onError: (err) => {
          toast.error(err.message);
          setName(initialName);
          setIsEditing(false);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setName(initialName);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="h-7 w-48"
        disabled={updateMut.isPending}
      />
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="group flex items-center gap-1.5 text-left font-medium hover:text-primary"
    >
      {initialName}
      <IconPencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

function UploadDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const createMut = useCreateRelease();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !name.trim()) return;

    createMut.mutate(
      { file, name: name.trim() },
      {
        onSuccess: () => {
          toast.success("Release created");
          onOpenChange(false);
          setName("");
          setFile(null);
        },
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload NVP_Data File</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label htmlFor="release-name">Release Name</Label>
            <Input
              id="release-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., v25, Season 6 Build"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="release-file">NVP_Data JSON File</Label>
            <Input
              id="release-file"
              type="file"
              accept=".json"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              required
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMut.isPending || !file || !name.trim()}>
              {createMut.isPending ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AdminReleasesPage() {
  const { data: releases, isLoading } = useReleases();
  const deleteMut = useDeleteRelease();
  const [uploadOpen, setUploadOpen] = useState(false);

  const handleDelete = (id: ReleaseId, name: string, usageCount: number) => {
    if (usageCount > 0) {
      toast.error("Cannot delete release that is in use by events");
      return;
    }
    if (confirm(`Delete release "${name}"?`)) {
      deleteMut.mutate(id, {
        onSuccess: () => toast.success("Release deleted"),
        onError: (err) => toast.error(err.message),
      });
    }
  };

  if (isLoading) {
    return (
      <div>
        <Skeleton className="h-8 w-48 rounded-lg" />
        <div className="mt-6 space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Manage Releases"
        action={
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <IconUpload className="mr-1.5 h-4 w-4" />
            New Release
          </Button>
        }
      />

      <div className="mt-6">
        {!releases || releases.length === 0 ? (
          <EmptyState
            icon={IconPackage}
            message="No releases uploaded yet"
            className="py-16"
          />
        ) : (
          <div className="flex flex-col gap-2">
            {releases.map((release) => {
              const inUse = release.usage_count > 0;
              return (
                <div
                  key={release.id}
                  className={`flex items-center bg-card border-l-3 rounded-r-lg pl-4 pr-5 py-3 transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)] ${
                    inUse ? "border-l-blue-400" : "border-l-border"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <EditableName
                        releaseId={asReleaseId(release.id)}
                        initialName={release.name}
                      />
                      <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                        v{release.build_version}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {formatDate(release.created_at)}
                      <span className="text-muted-foreground/40 mx-2">|</span>
                      by {release.created_by}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {inUse ? (
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                        {release.usage_count} event{release.usage_count !== 1 ? "s" : ""}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Unused</span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleDelete(
                          asReleaseId(release.id),
                          release.name,
                          release.usage_count
                        )
                      }
                      disabled={deleteMut.isPending || release.usage_count > 0}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <IconTrash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </>
  );
}
